import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'USER' | 'ADMIN' | 'MASTER' | 'SUPER_ADMIN';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean | 'mfa_required'>;
  register: (email: string, name: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
} 

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    setUser(null);
    tokenStorage.clearTokens();
    delete api.defaults.headers.common['Authorization'];
    toast.error('Session expired. Please login again.');
    
    // Automatically redirect to login page
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500); // Small delay to show the toast
  }, []);

  useEffect(() => {
    // With httpOnly cookies, we can't check tokens in localStorage
    // Instead, we'll always try to verify with the backend
    verifyToken();

    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [handleLogout]);

  useEffect(() => {
    const sessionMonitor = setInterval(() => {
      // With httpOnly cookies, verify session with backend
      if (user) {
        verifyToken();
      }
    }, 60000); // Check every minute
    
    return () => {
      clearInterval(sessionMonitor);
    };
  }, [user, handleLogout]);

  const verifyToken = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data.user);
    } catch (error: any) {
      tokenStorage.clearTokens();
      delete api.defaults.headers.common['Authorization'];
      setUser(null);

      // Check if blocked by IP whitelist
      if (error.response?.status === 403 && error.response?.data?.code === 'IP_NOT_WHITELISTED') {
        toast.error('Access denied: Your IP address is not authorized');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean | 'mfa_required'> => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      
      // Check if MFA is required
      if (response.data.requiresMfa || response.data.requiresMFA) {
        return 'mfa_required';
      }
      
      const { user } = response.data;
      // With httpOnly cookies, tokens are not returned in response body
      // They're automatically set as cookies by the browser
      const { accessToken, refreshToken } = response.data;
      
      if (!user) {
        throw new Error('Invalid login response');
      }
      
      // Only store tokens if they're returned (backward compatibility)
      if (accessToken && refreshToken) {
        tokenStorage.setTokens(accessToken, refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      }
      
      setUser(user);
      
      toast.success('Login successful');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      return false;
    }
  };

  const register = async (email: string, name: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/api/auth/register', { email, name, password });
      const { user } = response.data;
      // With httpOnly cookies, tokens are not returned in response body
      const { accessToken, refreshToken } = response.data;
      
      // Only store tokens if they're returned (backward compatibility)
      if (accessToken && refreshToken) {
        tokenStorage.setTokens(accessToken, refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      }
      
      setUser(user);
      
      toast.success('Registration successful');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate server-side sessions
      await api.post('/api/auth/logout', { logoutAll: false });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear client-side state
      tokenStorage.clearTokens();
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};